import { keepPreviousData } from '@tanstack/react-query';
import { PlatformProfileResult, useRepositoryQuery } from '@mews-commander/data-access';
import { LocalizedPageTitle, PortfolioLayout, PortfolioLayoutHeader } from '@mews-commander/ui';
import { LocalizedText, useUrlSyncedTableState } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    StringFilterType,
    Table,
    TableActiveColumnFilters,
    TableHeader,
    TableProvider,
    TableState,
} from '@optimus-web/core';
import { platformProfileColumns } from './PlatformProfilesTable';
import { NewPlatformProfileButton } from './NewPlatformProfile';

export const PlatformProfiles: React.FC = () => {
    const [tableState, setTableState] = useUrlSyncedTableState<PlatformProfileResult>({
        columns: platformProfileColumns,
        pageSize: 50,
    });
    const { totalRowCount, platformProfiles, isFetching } = usePlatformProfilesData(tableState);

    return (
        <PortfolioLayout>
            <PortfolioLayoutHeader title={<LocalizedPageTitle textKey={TextKey.PlatformProfilePlural} />} />
            <TableProvider columns={platformProfileColumns} state={tableState} onStateChange={setTableState} as="div">
                <TableHeader
                    title={<LocalizedText textKey={TextKey.UserPlural} />}
                    subTitle={totalRowCount}
                    globalSearch={{ placeholder: <LocalizedText textKey={TextKey.UsersSearchHint} /> }}
                    rightSide={<NewPlatformProfileButton />}
                />
                <TableActiveColumnFilters />
                <Table
                    uniqueRowId="profileId"
                    data={platformProfiles}
                    amountOfRows={totalRowCount}
                    loading={isFetching}
                    fixedFirstColumn
                />
            </TableProvider>
        </PortfolioLayout>
    );
};

const usePlatformProfilesData = (tableState: TableState<PlatformProfileResult>) => {
    const { pagination, globalFilters } = tableState;

    const { data: tableData, isFetching } = useRepositoryQuery(
        'platformProfiles',
        'getAll',
        {
            pagination,
            ...(globalFilters.search && {
                globalSearchFilter: { value: globalFilters.search, filterType: StringFilterType.Contains },
            }),
        },
        {
            placeholderData: keepPreviousData,
        }
    );
    const totalRowCount = tableData?.totalRowCount || 0;
    const platformProfiles = tableData?.data || [];

    return { platformProfiles, totalRowCount, isFetching };
};
